import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminLogin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Admin Login | Orange Onderhoudsbedrijf';
    const token = localStorage.getItem('adminToken');
    if (token) navigate('/admin/dashboard');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem('adminToken', res.data.token);
      localStorage.setItem('adminUser', res.data.username);
      navigate('/admin/dashboard');
    } catch {
      setError(t('admin.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#FF5F00] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-black text-2xl">O</span>
          </div>
          <h1 className="text-2xl font-black text-[#0F172A]">Orange Onderhoudsbedrijf</h1>
          <p className="text-[#64748B] text-sm mt-1">Admin Panel</p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="admin-login-form"
          className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm space-y-5"
        >
          <h2 className="text-xl font-bold text-[#0F172A]">Login</h2>

          <div>
            <label className="block text-sm font-semibold text-[#334155] mb-2">Username</label>
            <input
              type="text"
              required
              value={form.username}
              onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
              data-testid="admin-username-input"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[#0F172A] focus:outline-none focus:border-[#FF5F00] transition-all text-sm"
              placeholder="username"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#334155] mb-2">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                required
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                data-testid="admin-password-input"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-12 text-[#0F172A] focus:outline-none focus:border-[#FF5F00] transition-all text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="admin-login-error" className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-[#FF5F00] text-white py-4 rounded-xl font-bold hover:bg-[#EA580C] transition-all disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
          >
            {loading ? 'Loading...' : 'Login'}
          </button>
        </form>

        <div className="text-center mt-6">
          <Link to="/" className="text-[#64748B] text-sm hover:text-[#FF5F00] transition-colors">
            ← Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
